import { useReactTable, getCoreRowModel, getSortedRowModel, getPaginationRowModel, getExpandedRowModel, flexRender } from '@tanstack/react-table';
import React__default, { useCallback, useEffect } from 'react';
import { HeaderCell } from './HeaderCell.js';
import { LinkCell, ButtonCell, INTERACTIVE_CELL_TEXT_UNDERLINE_DATA_ATTR, INTERACTIVE_CELL_DATA_ATTR, Table as Table$1, THeaderRow, getCellStyles, Resizer } from './styles.js';
import { MemoizedTableBodyWindowed, TableBodyWindowed, MemoizedTableBodyRegular, TableBodyRegular } from './TableBody.js';
import { TableHeader } from './TableHeader.js';
import { TablePagination } from './TablePagination.js';
import { jsxs, jsx } from '@emotion/react/jsx-runtime';

function useHandleRowClick(onRowClick, excludeColumnsFromRowClick, table, renderSubComponent, accordionMode) {
  return useCallback(e => {
    if (!(e.target instanceof HTMLElement)) return;
    if (e.target.closest('a, button')) return;
    const clickedCell = e.target.closest('td');
    const columnId = clickedCell === null || clickedCell === void 0 ? void 0 : clickedCell.dataset.columnId;
    if (!clickedCell || excludeColumnsFromRowClick !== null && excludeColumnsFromRowClick !== void 0 && excludeColumnsFromRowClick.includes(columnId)) return;
    const rowId = e.currentTarget.dataset.id;
    if (!rowId) return;
    const row = table.getRow(rowId);
    if (renderSubComponent && row.getCanExpand()) {
      if (accordionMode) {
        const isCurrentlyExpanded = row.getIsExpanded();
        table.resetExpanded();
        if (!isCurrentlyExpanded) {
          row.toggleExpanded();
        }
      } else {
        row.toggleExpanded();
      }
    }
    if (onRowClick) {
      onRowClick(row.original, row.index);
    }
  }, [onRowClick, excludeColumnsFromRowClick, table, renderSubComponent, accordionMode]);
}
const DEFAULT_COLUMN = {
  minSize: 60,
  maxSize: 500
};
function Component({
  ['data-testid']: dataTestId,
  className,
  columns,
  data,
  density,
  defaultColumn,
  resetColumnSizing = false,
  hideHead,
  loading,
  loadingRowCount = 4,
  rowSize = 'lg',
  state,
  stickyHeaderTopOffset,
  handlePaginationChange,
  onSortingChange,
  windowed,
  infiniteScroll,
  onRowClick,
  excludeColumnsFromRowClick,
  columnResizeMode = 'onChange',
  resizable = false,
  getSizeUpdates,
  getRowCanExpand,
  renderSubComponent,
  accordionMode = false,
  getRowProps,
  expandAnimationDuration = 0.2,
  ...rest
}) {
  var _rest$initialState;
  const [sorting, setSorting] = React__default.useState([]);
  const [expanding, setExpanding] = React__default.useState({});
  const [dragging, setDragging] = React__default.useState(false);
  const handleSortingChange = React__default.useCallback(sortingState => {
    setSorting(sortingState);
    if (typeof onSortingChange === 'function') onSortingChange(sortingState);
  }, [onSortingChange]);
  const hasPagination = Boolean((_rest$initialState = rest.initialState) === null || _rest$initialState === void 0 ? void 0 : _rest$initialState.pagination);
  const rowData = React__default.useMemo(() => {
    if (loading && loadingRowCount > 0) {
      return Array.from({
        length: loadingRowCount
      }, (_, i) => ({
        index: i
      }));
    } else if (infiniteScroll !== null && infiniteScroll !== void 0 && infiniteScroll.loading) {
      var _infiniteScroll$loadi;
      return [...data, ...Array.from({
        length: (_infiniteScroll$loadi = infiniteScroll.loadingRowCount) !== null && _infiniteScroll$loadi !== void 0 ? _infiniteScroll$loadi : loadingRowCount
      }, (_, i) => ({
        index: i,
        loading: true
      }))];
    } else {
      return data;
    }
  }, [data, loadingRowCount, loading, infiniteScroll === null || infiniteScroll === void 0 ? void 0 : infiniteScroll.loading, infiniteScroll === null || infiniteScroll === void 0 ? void 0 : infiniteScroll.loadingRowCount]);
  const table = useReactTable({
    data: rowData,
    columns,
    columnResizeMode,
    defaultColumn: defaultColumn ? defaultColumn : DEFAULT_COLUMN,
    sortDescFirst: false,
    state: {
      sorting,
      expanded: expanding,
      ...state
    },
    onSortingChange: handleSortingChange,
    ...rest,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: hasPagination && !rest.manualPagination ? getPaginationRowModel() : undefined,
    getExpandedRowModel: renderSubComponent ? getExpandedRowModel() : undefined,
    enableExpanding: !!renderSubComponent,
    getRowCanExpand: getRowCanExpand ? row => getRowCanExpand(row.original) : renderSubComponent ? () => true : undefined,
    onExpandedChange: setExpanding
  });
  const paginationState = table.getState().pagination;
  const userColDefsById = React__default.useMemo(() => table.getAllColumns().reduce((acc, col, i) => {
    acc[col.id] = table._getColumnDefs()[i];
    return acc;
  }, {}), [table, columns]);
  const handleRowClick = useHandleRowClick(onRowClick, excludeColumnsFromRowClick, table, renderSubComponent, accordionMode);
  const previousPage = () => {
    rest.manualPagination && handlePaginationChange && handlePaginationChange(paginationState.pageIndex - 1);
    table.previousPage();
  };
  const nextPage = () => {
    rest.manualPagination && handlePaginationChange && handlePaginationChange(paginationState.pageIndex + 1);
    table.nextPage();
  };
  const headers = table.getFlatHeaders();
  const columnSizing = table.getState().columnSizing;
  const columnSizingInfo = table.getState().columnSizingInfo;
  const columnSizeVars = React__default.useMemo(() => {
    if (resizable) {
      const colSizes = {};
      const sizesById = {};
      for (let i = 0; i < headers.length; i++) {
        const header = headers[i];
        const columnSize = header.column.getSize();
        colSizes[`--header-${header.id}-size`] = header.getSize();
        colSizes[`--col-${header.column.id}-size`] = columnSize;
        sizesById[header.column.id] = columnSize;
      }
      return {
        css: colSizes,
        ids: sizesById
      };
    } else return {};
  }, [columnSizing, columnSizingInfo, columns]);
  const gapCount = density ? headers.length - 1 : 0;
  const rowCount = table.getRowModel().rows.length;
  const gapSize = density === 'sm' ? 8 : 12;
  const isResizing = table.getState().columnSizingInfo.isResizingColumn;
  const TableBody = windowed ? isResizing ? MemoizedTableBodyWindowed : TableBodyWindowed : isResizing ? MemoizedTableBodyRegular : TableBodyRegular;
  const handleResizeEnd = useCallback(() => {
    setDragging(false);
    getSizeUpdates && getSizeUpdates(columnSizeVars.ids);
  }, [columnSizeVars.ids, setDragging, getSizeUpdates]);
  useEffect(() => {
    if (dragging) {
      document.addEventListener('pointerup', handleResizeEnd);
      return () => {
        document.removeEventListener('pointerup', handleResizeEnd);
      };
    }
  }, [dragging, handleResizeEnd]);
  useEffect(() => {
    if (resetColumnSizing) {
      let colDefSize = 0;
      columns.forEach(column => {
        colDefSize += column.size ? column.size : 0;
      });
      table.getTotalSize() !== colDefSize && table.resetColumnSizing();
    }
  }, [resetColumnSizing, columns, table]);
  return jsxs(React__default.Fragment, {
    children: [jsxs(Table$1, {
      className: className,
      "data-testid": dataTestId,
      style: {
        ...columnSizeVars.css,
        width: resizable && table.getTotalSize() + gapCount * gapSize
      },
      children: [jsx(TableHeader, {
        offset: stickyHeaderTopOffset,
        visiblyHidden: hideHead,
        children: table.getHeaderGroups().map(headerGroup => jsx(THeaderRow, {
          density: density,
          children: headerGroup.headers.map(header => {
            const {
              column,
              colSpan,
              id,
              getContext
            } = header;
            return jsx(HeaderCell, {
              visiblyHidden: column.columnDef.hideHeader,
              colSpan: colSpan,
              justifyContent: column.columnDef.align,
              sortDirection: column.getIsSorted(),
              style: getCellStyles(column, userColDefsById[column.id]),
              onSortingChange: column.getCanSort() ? column.getToggleSortingHandler() : undefined,
              "data-column-id": column.id,
              density: density,
              resizeHandle: resizable && jsx(Resizer, {
                onDoubleClick: () => header.column.resetSize(),
                onPointerDown: () => setDragging(true),
                onMouseDown: header.getResizeHandler(),
                onTouchStart: header.getResizeHandler(),
                isResizing: header.column.getIsResizing(),
                className: `resizer`,
                density,
                rowCount
              }),
              ...(resizable ? {
                style: {
                  width: `calc(var(--header-${id}-size) * 1px)`
                }
              } : {}),
              children: flexRender(column.columnDef.header, getContext())
            }, id);
          })
        }, headerGroup.id))
      }), jsx(TableBody, {
        table: table,
        rowSize: rowSize,
        loading: loading,
        userColDefsById: userColDefsById,
        visibleColumnCount: table.getVisibleFlatColumns().length,
        scrollRef: infiniteScroll === null || infiniteScroll === void 0 ? void 0 : infiniteScroll.scrollRef,
        density: density,
        handleRowClick: renderSubComponent || onRowClick ? handleRowClick : undefined,
        resizable: resizable,
        enableExpanding: !!renderSubComponent,
        renderSubComponent: renderSubComponent,
        getRowProps: getRowProps,
        expandAnimationDuration: expandAnimationDuration
      })]
    }), hasPagination && jsx(TablePagination, {
      loading: loading,
      nextPage: nextPage,
      previousPage: previousPage,
      getCanNextPage: table.getCanNextPage,
      getCanPreviousPage: table.getCanPreviousPage,
      getPageCount: table.getPageCount,
      paginationState: paginationState
    })]
  });
}
const Table = Object.assign(Component, {
  LinkCell,
  ButtonCell,
  INTERACTIVE_CELL_TEXT_UNDERLINE_DATA_ATTR,
  INTERACTIVE_CELL_DATA_ATTR
});

export { Table };
